import { skipToken } from '@reduxjs/toolkit/query';
import { Alert, Form, Modal, Spin } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetScopeQuery, useUpdateScopeMutation } from '../../apis/apiScopes';
import ScopeCreateForm from '../../components/domain/Scopes/ScopeCreateForm';
import type { ScopeCreateFormValues } from '../../components/domain/Scopes/ScopeCreateForm';
import { getRequestErrorMessage } from '../../lib/utils/getRequestErrorMessage';
import { useStableParams } from '../../routes/useStableParams';
import { ReduxState, useAppDispatch } from '../../store';
import { AvailableModals, closeModal } from './modalSlice';

const modalId = AvailableModals.SCOPE_ATTRIBUTES_EDIT;

const ScopeAttributesEditModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { projectShortName } = useStableParams();
  const scopeId = useSelector((state: ReduxState) => state.modal.modalData[modalId]?.scopeId);
  const [form] = Form.useForm<ScopeCreateFormValues>();
  const [submitError, setSubmitError] = useState<string>();
  const {
    data: scope,
    isError,
    isFetching,
  } = useGetScopeQuery(
    projectShortName && scopeId !== undefined ? { projectShortName, scopeId } : skipToken,
  );
  const [updateScope, updateState] = useUpdateScopeMutation();

  const close = () => dispatch(closeModal());
  const submit = async (name: string) => {
    if (scopeId === undefined) return;

    try {
      setSubmitError(undefined);
      await updateScope({ scopeId, request: { name } }).unwrap();
      close();
    } catch (error) {
      setSubmitError(getRequestErrorMessage(error, 'The Scope attributes could not be updated.'));
    }
  };

  const isLoading = isFetching && !scope;
  const cannotLoadScope = isError || !scope;

  return (
    <Modal
      cancelButtonProps={{ disabled: updateState.isLoading }}
      confirmLoading={updateState.isLoading}
      maskClosable={false}
      okButtonProps={{ disabled: cannotLoadScope }}
      okText="Save"
      open={true}
      title="Edit Scope Attributes"
      onCancel={close}
      onOk={() => form.submit()}
    >
      {isLoading && <Spin />}
      {!isLoading && cannotLoadScope && (
        <Alert message="The selected Scope could not be loaded." showIcon type="error" />
      )}
      {!isLoading && scope && (
        <ScopeCreateForm
          autoFocus
          error={submitError}
          form={form}
          initialName={scope.name}
          inputDisabled={updateState.isLoading}
          onSubmit={submit}
        />
      )}
    </Modal>
  );
};

export default ScopeAttributesEditModal;
